import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SensorData } from '../types';
import { 
  formatSensorValue, 
  getSensorIcon, 
  getSensorDisplayName, 
  getSensorColor, 
  formatTimestamp 
} from '../utils/formatters';
import { Activity, Thermometer, Gauge, Vibrate, MapPin, Radio, BarChart3 } from 'lucide-react';
import SensorChart from './SensorChart';

interface GenericSensorProps {
  sensor: SensorData;
}

const GenericSensor: React.FC<GenericSensorProps> = ({ sensor }) => {
  const [showChart, setShowChart] = useState(false);
  const value = sensor.current_value?.value || 0;
  const isOnline = sensor.device_info?.status || false;
  const lastUpdate = formatTimestamp(sensor.current_value?.timestamp || null);
  const hasData = sensor.current_value?.value !== null && sensor.current_value?.value !== undefined;
  
  const getSensorIconComponent = (type: SensorData['type']) => {
    const iconProps = { className: 'w-8 h-8 text-white' };
    
    switch (type) {
      case 'temperature':
        return <Thermometer {...iconProps} />;
      case 'pressure':
        return <Gauge {...iconProps} />;
      case 'vibration':
        return <Vibrate {...iconProps} />;
      case 'speed':
        return <Activity {...iconProps} />;
      case 'gps':
        return <MapPin {...iconProps} />;
      default:
        return <Radio {...iconProps} />;
    }
  };

  const getValueColor = (type: SensorData['type'], value: number) => {
    const min = sensor.display_config?.min_value || 0;
    const max = sensor.display_config?.max_value || 100;
    const percentage = ((value - min) / (max - min)) * 100;
    
    switch (type) {
      case 'temperature':
        if (percentage > 80) return 'text-red-600';
        if (percentage > 60) return 'text-orange-600';
        return 'text-blue-600';
      case 'pressure':
        if (percentage < 20 || percentage > 90) return 'text-red-600';
        if (percentage < 40 || percentage > 70) return 'text-yellow-600';
        return 'text-green-600';
      case 'vibration':
        if (percentage > 70) return 'text-red-600';
        if (percentage > 50) return 'text-yellow-600';
        return 'text-green-600';
      default:
        return 'text-gray-900';
    }
  };

  const getGaugePercentage = () => {
    const min = sensor.display_config?.min_value || 0;
    const max = sensor.display_config?.max_value || 100;
    return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
  };

  const percentage = getGaugePercentage();

  return (
    <>
      <motion.div
        className="sensor-card cursor-pointer"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        onClick={() => setShowChart(true)}
        whileHover={{ scale: 1.02 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-3 rounded-lg ${getSensorColor(sensor.type)}`}>
              {getSensorIconComponent(sensor.type)}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{sensor.name}</h3>
              <p className="text-sm text-gray-500">{getSensorDisplayName(sensor.type)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowChart(true);
              }}
              className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
              title="View Chart"
            >
              <BarChart3 className="w-4 h-4" />
            </button>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              isOnline && hasData ? 'bg-green-100 text-green-800' : 
              isOnline && !hasData ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {isOnline && hasData ? 'Online' : 
               isOnline && !hasData ? 'No Data' : 
               'Offline'}
            </div>
          </div>
        </div>

      {/* Value Display */}
      <div className="text-center mb-6">
        <div className={`text-4xl font-bold mb-2 ${hasData ? getValueColor(sensor.type, value) : 'text-gray-400'}`}>
          {hasData ? formatSensorValue(sensor) : 'No Data'}
        </div>
        
        {/* Gauge for applicable sensors */}
        {hasData && (['temperature', 'pressure', 'vibration'].includes(sensor.type)) && (
          <div className="relative w-32 h-16 mx-auto mb-4">
            <svg className="w-full h-full" viewBox="0 0 120 60">
              {/* Background arc */}
              <path
                d="M 10 50 A 50 50 0 0 1 110 50"
                fill="transparent"
                stroke="#e5e7eb"
                strokeWidth="8"
                strokeLinecap="round"
              />
              {/* Value arc */}
              <motion.path
                d={`M 10 50 A 50 50 0 0 1 ${10 + (percentage / 100) * 100} ${50 - Math.sin((percentage / 100) * Math.PI) * 50}`}
                fill="transparent"
                stroke={sensor.type === 'temperature' ? '#f59e0b' : 
                       sensor.type === 'pressure' ? '#8b5cf6' : '#eab308'}
                strokeWidth="8"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
              />
            </svg>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-sm text-gray-600">
              {percentage.toFixed(1)}%
            </div>
          </div>
        )}
      </div>

      {/* GPS Coordinates for GPS sensors */}
      {hasData && sensor.type === 'gps' && sensor.device_info?.coordinates && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <div className="text-sm text-blue-800">
            <div>Lat: {sensor.device_info.coordinates.lat?.toFixed(6) || 'N/A'}</div>
            <div>Lng: {sensor.device_info.coordinates.lng?.toFixed(6) || 'N/A'}</div>
          </div>
        </div>
      )}

      {/* Additional Info */}
      <div className="space-y-2 text-sm text-gray-600">
        <div className="flex justify-between">
          <span>Device:</span>
          <span className="font-medium">{sensor.device_info?.name || 'Unknown'}</span>
        </div>
        <div className="flex justify-between">
          <span>Last Update:</span>
          <span className="font-medium">{lastUpdate}</span>
        </div>
        <div className="flex justify-between">
          <span>Group:</span>
          <span className="font-medium">{sensor.group.name}</span>
        </div>
        {sensor.location && (
          <div className="flex justify-between">
            <span>Location:</span>
            <span className="font-medium flex items-center">
              <MapPin className="w-3 h-3 mr-1" />
              {sensor.location}
            </span>
          </div>
        )}
        {sensor.display_config?.min_value !== undefined && sensor.display_config?.max_value !== undefined && (
          <div className="flex justify-between">
            <span>Range:</span>
            <span className="font-medium">
              {sensor.display_config.min_value} - {sensor.display_config.max_value} {sensor.display_config.unit || ''}
            </span>
          </div>
        )}
      </div>

      {/* Alert for out-of-range values */}
      {hasData && (
        (sensor.display_config?.min_value !== undefined && value < sensor.display_config.min_value) ||
        (sensor.display_config?.max_value !== undefined && value > sensor.display_config.max_value)
      ) ? (
        <motion.div
          className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center space-x-2">
            <Activity className="w-4 h-4 text-red-600" />
            <span className="text-red-800 text-sm font-medium">Value out of normal range</span>
          </div>
        </motion.div>
      ) : null}

      {/* No Data Warning */}
      {!hasData && (
        <motion.div
          className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center space-x-2">
            <Activity className="w-4 h-4 text-gray-600" />
            <span className="text-gray-800 text-sm font-medium">
              {isOnline ? 'Sensor data not available' : 'Device offline'}
            </span>
          </div>
        </motion.div>
      )}
      </motion.div>

      {/* Chart Modal */}
      {showChart && (
        <SensorChart
          sensorId={sensor.id}
          sensorName={sensor.name}
          sensorType={sensor.type}
          onClose={() => setShowChart(false)}
        />
      )}
    </>
  );
};

export default GenericSensor;